/**
 * WeaveIn.ai Platform - Contacts API Service
 * TypeScript service layer for contact / pre-member management
 */

import axios, { AxiosInstance } from 'axios';
import { API_VERSION } from './organizations';
import { initializeActiveCboId } from '../../utils/auth';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ContactCreate {
  first_name: string;
  last_name?: string;
  email?: string;
  phone_no?: string;
  dob?: string;
  gender?: string;
  race?: string;
  ethnicity?: string;
  language?: string;
  preferred_language?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  intake_source?: string;
  intake_source_detail?: Record<string, any>;
  assigned_to?: number;
  program_id?: number;
  tags?: string[];
  custom_fields?: Array<{ field_name: string; value: any }>;
}

export interface ContactUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_no?: string;
  dob?: string;
  gender?: string;
  race?: string;
  ethnicity?: string;
  language?: string;
  preferred_language?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  assigned_to?: number;
  intake_source?: string;
  intake_source_detail?: Record<string, any>;
}

export interface Contact {
  id: number;
  member_number?: string;
  record_type: 'contact' | 'member';
  first_name: string;
  last_name?: string;
  email?: string;
  phone_no?: string;
  date_of_birth?: string;
  gender?: string;
  race?: string;
  ethnicity?: string;
  language?: string;
  preferred_language?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  intake_source?: string;
  intake_source_detail?: Record<string, any>;
  intake_date?: string;
  // Form submitter envelope ("Your Name/Email/Phone") — who entered the form
  // (the contact themselves, or someone on their behalf).
  submitted_by_name?: string;
  submitted_by_email?: string;
  submitted_by_phone?: string;
  assigned_to?: number;
  data_quality_score?: number;
  custom_fields?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  pending_duplicates?: number;
  program_statuses?: ContactProgramStatus[];
  duplicate_matches?: DuplicateMatch[];
}

export interface ContactProgramStatus {
  id: number;
  program_id: number;
  program_name?: string;
  status: string;
  assigned_to?: number;
  assigned_to_name?: string;
  priority?: string;
  qualification_score?: number;
  eligibility_verified?: boolean;
  promoted_to_enrollment_id?: number;
  tags?: string[];
  custom_data?: Record<string, any>;
  created_at?: string;
  status_changed_at?: string;
}

export interface ContactActivity {
  id: number;
  activity_type: string;
  activity_name: string;
  activity_description?: string;
  activity_date?: string;
  duration_minutes?: number;
  contact_method?: string;
  contact_outcome?: string;
  contact_direction?: string;
  performed_by?: number;
  performed_by_name?: string;
  status?: string;
  is_billable?: boolean;
  notes?: string;
  program_id?: number;
  created_at?: string;
}

export interface ContactActivityCreate {
  activity_type: string;
  activity_name: string;
  activity_description?: string;
  activity_date?: string;
  duration_minutes?: number;
  contact_method?: string;
  contact_outcome?: string;
  contact_direction?: string;
  program_id?: number;
  notes?: string;
  is_billable?: boolean;
  billing_code?: string;
  status?: string;
  metadata?: Record<string, any>;
}

export interface DuplicateMatch {
  id: number;
  matched_member_id: number;
  match_score: number;
  match_fields?: Record<string, any>;
  matched_record_type?: string;
  resolution?: string;
  resolved_by?: number;
  resolved_at?: string;
  resolution_notes?: string;
  matched_first_name?: string;
  matched_last_name?: string;
  matched_member_number?: string;
  matched_email_masked?: string;
  matched_phone_masked?: string;
  matched_dob?: string;
}

export interface DuplicateCheckRequest {
  first_name: string;
  last_name?: string;
  phone?: string;
  email?: string;
  dob?: string;
}

export interface DuplicateCheckResult {
  member_id: number;
  first_name?: string;
  last_name?: string;
  phone_masked?: string;
  email_masked?: string;
  date_of_birth?: string;
  member_number?: string;
  record_type?: string;
  data_quality_score?: number;
  match_score: number;
  match_fields?: Record<string, { score: number; matched: boolean }>;
}

export interface ContactListResponse {
  items: Contact[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ContactListParams {
  page?: number;
  page_size?: number;
  search?: string;
  program_id?: number;
  status?: string;
  assigned_to?: number;
  intake_source?: string;
  created_after?: string;
  created_before?: string;
}

export interface StatusConfigItem {
  id: number;
  status_code: string;
  status_label: string;
  status_description?: string;
  sort_order: number;
  status_color?: string;
  is_terminal: boolean;
  promotes_to_enrollment: boolean;
  required_member_fields: string[];
  required_identifier_types: string[];
  required_form_template_ids: number[];
  allowed_next_statuses: string[];
  auto_assign_role?: string;
  auto_create_workflow_template_id?: number;
  auto_notification_template?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export interface StatusConfigCreate {
  status_code: string;
  status_label: string;
  status_description?: string;
  sort_order: number;
  status_color?: string;
  is_terminal?: boolean;
  promotes_to_enrollment?: boolean;
  required_member_fields?: string[];
  required_identifier_types?: string[];
  required_form_template_ids?: number[];
  allowed_next_statuses?: string[];
  auto_assign_role?: string;
  auto_create_workflow_template_id?: number;
  auto_notification_template?: Record<string, any>;
}

export interface PromoteRequest {
  enrollment_status?: string;
  assigned_to?: number;
}

// Notes, Goals, Documents, Tasks, Conversations, Forms, Workflows

export interface ContactNote {
  id: number;
  note_type: string;
  subject?: string;
  note_text: string;
  contact_method?: string;
  contact_duration_minutes?: number;
  requires_followup: boolean;
  followup_date?: string;
  is_confidential: boolean;
  created_by?: number;
  created_by_name?: string;
  created_at?: string;
  program_id?: number;
}

export interface ContactNoteCreate {
  note_type?: string;
  subject?: string;
  note_text: string;
  contact_method?: string;
  contact_duration_minutes?: number;
  requires_followup?: boolean;
  followup_date?: string;
  is_confidential?: boolean;
  program_id?: number;
}

export interface ContactGoal {
  id: number;
  goal_text: string;
  goal_category?: string;
  status: string;
  target_date?: string;
  completed_date?: string;
  program_id?: number;
  created_by?: number;
  created_by_name?: string;
  created_at?: string;
}

export interface ContactGoalCreate {
  goal_text: string;
  goal_category?: string;
  status?: string;
  target_date?: string;
  program_id?: number;
}

export interface ContactDocument {
  id: number;
  uuid: string;
  doc_type_id: number;
  doc_type_name?: string;
  notes?: string;
  program_id?: number;
  created_at?: string;
  created_by?: number;
  created_by_name?: string;
  file_name?: string;
  mime_type?: string;
}

export interface ContactTask {
  id: number;
  title: string;
  description?: string;
  task_type: string;
  status: string;
  priority: string;
  deadline?: string;
  assigned_to?: number;
  assigned_to_name?: string;
  program_id?: number;
  created_at?: string;
}

export interface ContactTaskCreate {
  title: string;
  description?: string;
  task_type?: string;
  priority?: string;
  deadline?: string;
  assigned_to?: number;
  program_id?: number;
}

export interface ContactConversations {
  channels: Array<{
    id: number;
    channel_name?: string;
    engagement_state?: string;
    engagement_score?: number;
    last_message_at?: string;
    member_response_count?: number;
    staff_message_count?: number;
  }>;
  recent_communications: Array<{
    id: number;
    communication_type?: string;
    channel?: string;
    status?: string;
    sent_at?: string;
    subject?: string;
    content?: string;
    sent_by_name?: string;
  }>;
}

export interface ContactSendMessage {
  channel: string;
  message: string;
  subject?: string;
  template_id?: number;
}

export interface ContactFormInstance {
  id: number;
  form_template_id: number;
  template_name?: string;
  form_type?: string;
  status: string;
  completion_percentage?: number;
  due_date?: string;
  submitted_at?: string;
  created_at?: string;
  assigned_to_user_id?: number;
  assigned_to_name?: string;
}

export interface ContactWorkflow {
  id: number;
  workflow_template_id: number;
  template_name?: string;
  status: string;
  progress_percentage?: number;
  started_at?: string;
  completed_at?: string;
  current_step_id?: number;
  assigned_to?: number;
}

// ============================================================================
// CONTACTS API CLASS
// ============================================================================

class ContactsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    initializeActiveCboId();

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('[ContactsAPI] Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.response?.data?.detail || error.message,
        });
        return Promise.reject(error);
      }
    );
  }

  // ========================================================================
  // CONTACT CRUD
  // ========================================================================

  async createContact(data: ContactCreate): Promise<Contact & { duplicate_matches: DuplicateCheckResult[]; duplicate_count: number }> {
    const response = await this.client.post('/api/contacts', data);
    return response.data;
  }

  async listContacts(params?: ContactListParams): Promise<ContactListResponse> {
    const response = await this.client.get<ContactListResponse>('/api/contacts', { params });
    return response.data;
  }

  async getContact(contactId: number): Promise<Contact> {
    const response = await this.client.get<Contact>(`/api/contacts/${contactId}`);
    return response.data;
  }

  async updateContact(contactId: number, data: ContactUpdate): Promise<any> {
    const response = await this.client.put(`/api/contacts/${contactId}`, data);
    return response.data;
  }

  async archiveContact(contactId: number): Promise<any> {
    const response = await this.client.delete(`/api/contacts/${contactId}`);
    return response.data;
  }

  // ========================================================================
  // PROGRAM STATUS
  // ========================================================================

  async getContactPrograms(contactId: number): Promise<ContactProgramStatus[]> {
    const response = await this.client.get<ContactProgramStatus[]>(`/api/contacts/${contactId}/programs`);
    return response.data;
  }

  async updateContactStatus(contactId: number, programId: number, data: { new_status: string; reason?: string; notes?: string }): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/programs/${programId}/status`, data);
    return response.data;
  }

  async assignContactStaff(contactId: number, programId: number, data: { assigned_to: number; notes?: string }): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/programs/${programId}/assign`, data);
    return response.data;
  }

  // ========================================================================
  // PROMOTION
  // ========================================================================

  async promoteContact(contactId: number): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/promote`);
    return response.data;
  }

  /** Pre-promotion dedup check: likely existing members/contacts for this contact. */
  async promotionCheck(contactId: number): Promise<{ matches: DuplicateCheckResult[]; count: number; member_match_count: number }> {
    const response = await this.client.get(`/api/contacts/${contactId}/promotion-check`);
    return response.data;
  }

  async enrollContact(contactId: number, programId: number, data: PromoteRequest = {}): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/programs/${programId}/enroll`, data);
    return response.data;
  }

  // ========================================================================
  // ACTIVITIES
  // ========================================================================

  async createContactActivity(contactId: number, data: ContactActivityCreate): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/activities`, data);
    return response.data;
  }

  async listContactActivities(contactId: number, params?: { program_id?: number; page?: number; page_size?: number }): Promise<{ items: ContactActivity[]; total: number; page: number; page_size: number }> {
    const response = await this.client.get(`/api/contacts/${contactId}/activities`, { params });
    return response.data;
  }

  // ========================================================================
  // DUPLICATE DETECTION
  // ========================================================================

  async checkDuplicates(data: DuplicateCheckRequest): Promise<{ matches: DuplicateCheckResult[]; count: number }> {
    const response = await this.client.post('/api/contacts/check-duplicates', data);
    return response.data;
  }

  async getContactDuplicates(contactId: number): Promise<DuplicateMatch[]> {
    const response = await this.client.get<DuplicateMatch[]>(`/api/contacts/${contactId}/duplicates`);
    return response.data;
  }

  async resolveDuplicate(contactId: number, matchId: number, data: { resolution: string; notes?: string }): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/duplicates/${matchId}/resolve`, data);
    return response.data;
  }

  async mergeContact(sourceId: number, targetId: number): Promise<any> {
    const response = await this.client.post(`/api/contacts/${sourceId}/merge/${targetId}`);
    return response.data;
  }

  // ========================================================================
  // BULK IMPORT
  // ========================================================================

  async bulkImportContacts(contacts: ContactCreate[]): Promise<any> {
    const response = await this.client.post('/api/contacts/bulk-import', contacts);
    return response.data;
  }

  // ========================================================================
  // PROGRAM CONTACT STATUS CONFIG
  // ========================================================================

  async getStatusConfig(programId: number): Promise<StatusConfigItem[]> {
    const response = await this.client.get<StatusConfigItem[]>(`/api/programs/${programId}/contact-status-config`);
    return response.data;
  }

  async createStatusConfig(programId: number, data: StatusConfigCreate): Promise<any> {
    const response = await this.client.post(`/api/programs/${programId}/contact-status-config`, data);
    return response.data;
  }

  async updateStatusConfig(programId: number, configId: number, data: Partial<StatusConfigCreate>): Promise<any> {
    const response = await this.client.put(`/api/programs/${programId}/contact-status-config/${configId}`, data);
    return response.data;
  }

  async deleteStatusConfig(programId: number, configId: number): Promise<any> {
    const response = await this.client.delete(`/api/programs/${programId}/contact-status-config/${configId}`);
    return response.data;
  }

  async seedDefaultStatusConfig(programId: number): Promise<any> {
    const response = await this.client.post(`/api/programs/${programId}/contact-status-config/seed-defaults`);
    return response.data;
  }

  async reorderStatusConfig(programId: number, statusIds: number[]): Promise<any> {
    const response = await this.client.put(`/api/programs/${programId}/contact-status-config/reorder`, { status_ids: statusIds });
    return response.data;
  }

  // ========================================================================
  // NOTES
  // ========================================================================

  async createNote(contactId: number, data: ContactNoteCreate): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/notes`, data);
    return response.data;
  }

  async listNotes(contactId: number): Promise<ContactNote[]> {
    const response = await this.client.get<ContactNote[]>(`/api/contacts/${contactId}/notes`);
    return response.data;
  }

  // ========================================================================
  // GOALS
  // ========================================================================

  async createGoal(contactId: number, data: ContactGoalCreate): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/goals`, data);
    return response.data;
  }

  async listGoals(contactId: number): Promise<ContactGoal[]> {
    const response = await this.client.get<ContactGoal[]>(`/api/contacts/${contactId}/goals`);
    return response.data;
  }

  async updateGoal(contactId: number, goalId: number, data: { status?: string; goal_text?: string }): Promise<any> {
    const response = await this.client.put(`/api/contacts/${contactId}/goals/${goalId}`, data);
    return response.data;
  }

  // ========================================================================
  // DOCUMENTS
  // ========================================================================

  async listDocuments(contactId: number): Promise<ContactDocument[]> {
    const response = await this.client.get<ContactDocument[]>(`/api/contacts/${contactId}/documents`);
    return response.data;
  }

  async createDocument(contactId: number, data: { doc_type_id: number; notes?: string; program_id?: number }): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/documents`, data);
    return response.data;
  }

  async deleteDocument(contactId: number, docId: number): Promise<any> {
    const response = await this.client.delete(`/api/contacts/${contactId}/documents/${docId}`);
    return response.data;
  }

  // ========================================================================
  // TASKS
  // ========================================================================

  async listTasks(contactId: number): Promise<ContactTask[]> {
    const response = await this.client.get<ContactTask[]>(`/api/contacts/${contactId}/tasks`);
    return response.data;
  }

  async createTask(contactId: number, data: ContactTaskCreate): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/tasks`, data);
    return response.data;
  }

  // ========================================================================
  // CONVERSATIONS
  // ========================================================================

  async getConversations(contactId: number): Promise<ContactConversations> {
    const response = await this.client.get<ContactConversations>(`/api/contacts/${contactId}/conversations`);
    return response.data;
  }

  async sendMessage(contactId: number, data: ContactSendMessage): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/send-message`, data);
    return response.data;
  }

  // ========================================================================
  // FORMS
  // ========================================================================

  async listForms(contactId: number): Promise<ContactFormInstance[]> {
    const response = await this.client.get<ContactFormInstance[]>(`/api/contacts/${contactId}/forms`);
    return response.data;
  }

  async assignForm(contactId: number, data: { form_template_id: number; assigned_to_user_id?: number; due_date?: string }): Promise<any> {
    const response = await this.client.post(`/api/contacts/${contactId}/forms`, data);
    return response.data;
  }

  // ========================================================================
  // WORKFLOWS
  // ========================================================================

  async listWorkflows(contactId: number): Promise<ContactWorkflow[]> {
    const response = await this.client.get<ContactWorkflow[]>(`/api/contacts/${contactId}/workflows`);
    return response.data;
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

export const contactsAPI = new ContactsAPI();
export default contactsAPI;
